import {Platform, StyleSheet, type ViewStyle} from 'react-native';

import {useResolvedScheme} from '@/shared/ui/theme-mode';

/**
 * SamVision — токены кабинета.
 *
 * Это дизайн-система SamVision (`ui-kit/src/styles/tokens.css`), перенесённая в
 * React Native токен в токен. Язык оформления — рабочая станция видеонаблюдения в
 * духе Hikvision: тёмно-серые плоскости, тонкие линии вместо теней, прямые углы и
 * один фирменный красный акцент на весь интерфейс.
 *
 * ## Что изменилось, и почему
 *
 * Предыдущая тема была shadcn/ui: нейтральное шасси, бирюзовый `primary`, скругления
 * до 14pt и шкала теней Tailwind. Кабинет и рабочая станция при этом были двумя
 * продуктами одной компании, которые не узнавали друг друга. Теперь у них одна
 * система, и три её решения переносятся сюда без изменений:
 *
 *   1. КРАСНЫЙ — СИГНАЛ, А НЕ ФОН. Фирменный `#d71820` тратится скупо: заливка
 *      главной кнопки, риска активной вкладки, флажок во включённом состоянии, рамка
 *      поля в фокусе. Всё остальное — оттенки серого, чтобы красное было видно
 *      боковым зрением. Одна главная кнопка на экран.
 *
 *   2. ОПАСНОСТЬ — НЕ АКЦЕНТ. Тревога, ошибка, удаление — `destructive`, коралловый
 *      (`--danger`), светлее и теплее фирменного: отказ в таблице должен отличаться от
 *      выбранной строки. Выбор, фокус, главное действие — `primary`.
 *
 *   3. ГЛУБИНА — ЭТО ЛИНИЯ, А НЕ ТЕНЬ. Панели разделяются рамками и стыкуются друг с
 *      другом; тень остаётся только у всплывающего (меню, диалог, тост), чтобы оно
 *      отличалось от врезанного. Радиус до 4pt.
 *
 * Две темы. Тёмная — рабочее место оператора чаще стоит в полумраке рядом с
 * мониторами. Светлая — для дневной смены; она не подгоняется фильтрами, а описана
 * отдельным набором значений. ОПРАВА окна (боковая панель, верхняя полоса, панель
 * вкладок) — семейство `chrome*` — следует за темой: тёмно-серая в тёмной, светлая в
 * светлой. Рабочая станция (ui-kit) держит оправу тёмной в обеих темах, но кабинет
 * живёт на телефоне рядом с системными панелями, и тёмная полоса над светлой
 * страницей там читается не как «край прибора», а как чужая тема. Оправа всё равно
 * отличается от содержимого — на полтона и линией, — чтобы органы управления не
 * сливались с тем, чем управляют.
 *
 * Правило одно: компоненты не знают конкретных цветов. Они обращаются к смысловым
 * именам (`card`, `mutedForeground`, `destructive`), а тема подставляет значения.
 */

/**
 * A colour at partial alpha, as an 8-digit hex.
 *
 * ui-kit пишет такие как `rgba(215, 24, 32, 0.18)`; здесь нет CSS, поэтому они
 * считаются от своей же базы. React Native и react-native-web принимают `#rrggbbaa`.
 */
export function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  // Tolerate a value that already carries alpha: replace it rather than append.
  return hex.slice(0, 7) + a;
}

export type Palette = {
  /* ── Оправа ────────────────────────────────────────────────────────────
     `--chrome-*`: боковая панель, верхняя полоса, панель вкладок телефона. Своя
     поверхность в каждой теме — это рамка вокруг содержимого, а не само содержимое. */
  chrome: string;
  /** A control resting ON the frame — an icon plate, a quiet button. */
  chromeRaised: string;
  chromeForeground: string;
  chromeMuted: string;
  chromeBorder: string;
  /** Hover / press on the frame. */
  chromeHover: string;
  /** A recess in the frame — the context chip. */
  chromeSunken: string;
  /** Риска активного раздела на оправе — фирменный красный. */
  chromeAccent: string;

  /* ── Поверхности: от самой глубокой к всплывающей ─────────────────────── */
  /** `--surface-sunken` — под содержимым, глубже страницы (дорожка, врезка). */
  sunken: string;
  /** `--surface-base` — сама страница, и заливка поля ввода. */
  background: string;
  foreground: string;
  /** `--surface-raised` — панель на странице. */
  card: string;
  cardForeground: string;
  /** `--surface-overlay` — всплывающее: меню, диалог, тост. */
  elevated: string;
  /** Alias of `elevated`. */
  popover: string;
  /** `--surface-inset` — полоса заголовка панели, шапка таблицы, подвал. */
  inset: string;
  /** `--surface-active` — выбранная строка. */
  active: string;

  /* ── Краска и действие ─────────────────────────────────────────────────── */
  /**
   * Фирменный красный, `--accent`: заливка главной кнопки, риска активной вкладки,
   * включённый флажок, рамка поля в фокусе. Больше ничего.
   */
  primary: string;
  primaryForeground: string;
  /** Primary AS TEXT — a link, an active tab label. Never the fill colour. */
  primaryInk: string;
  /** `--accent-soft` — мягкая заливка акцентом: включённый переключатель, метка. */
  primarySoft: string;
  /** `--accent-softer` — нажатие на обычную кнопку. */
  primarySofter: string;

  secondary: string;
  secondaryForeground: string;
  /** `--text-secondary` — подписи полей, шапка таблицы, значки в заголовках. */
  textSecondary: string;
  muted: string;
  mutedForeground: string;
  /** `--surface-hover` — наведение и нажатие. */
  accent: string;
  accentForeground: string;

  /* ── Статусы ───────────────────────────────────────────────────────────── */
  /** `--success` — на связи / проход разрешён. */
  signal: string;
  signalForeground: string;
  signalInk: string;
  /** `*-soft` — фон метки состояния. Значение с альфой: метка ложится на любую поверхность. */
  signalSoft: string;
  warning: string;
  warningForeground: string;
  warningInk: string;
  warningSoft: string;
  /** `--danger` — коралловый, не фирменный красный. */
  destructive: string;
  destructiveForeground: string;
  destructiveInk: string;
  destructiveSoft: string;
  /** `--info` — «важно», не «плохо»: главное число без тревоги, справка. */
  info: string;
  infoInk: string;
  infoSoft: string;
  /** `--neutral-soft` — метка без состояния. */
  neutralSoft: string;

  /* ── Линии ─────────────────────────────────────────────────────────────── */
  /** `--border-subtle` — линии между строками. */
  borderSubtle: string;
  /** `--border-default` — рамка панели, поля, таблицы. */
  border: string;
  /** `--border-strong` — рамка обычной кнопки, флажка, ползунок прокрутки. */
  borderStrong: string;
  /** Рамка контрола в покое. Поле отличается от карточки ЗАЛИВКОЙ (`background`), а не только линией. */
  input: string;
  /** Focus ring — the accent. */
  ring: string;

  /** Header row of a data grid. */
  gridHeader: string;
  /** The strip of verbs above a grid, and a panel's footer. */
  toolbar: string;

  /** `--scrim` — за модальным окном. */
  overlay: string;
  /** `--skeleton` — заглушка загрузки. */
  skeleton: string;
};

/** Фирменные цвета марки — одни на обе темы. */
export const BRAND = {
  red: '#d71820',
  redHover: '#e8353c',
  redActive: '#b9141b',
  gray: '#6c6e71',
} as const;

export const dark: Palette = {
  chrome: '#1c1c1c',
  chromeRaised: '#242424',
  chromeForeground: '#e0e0e0',
  chromeMuted: '#8c8c8c',
  chromeBorder: '#ffffff14',
  chromeHover: '#ffffff12',
  chromeSunken: '#00000059',
  chromeAccent: BRAND.red,

  sunken: '#1f1f1f',
  background: '#2b2b2b',
  foreground: '#e8e8e8',
  card: '#333333',
  cardForeground: '#e8e8e8',
  elevated: '#3d3d3d',
  popover: '#3d3d3d',
  inset: '#00000038',
  active: '#ffffff1a',

  primary: BRAND.red,
  primaryForeground: '#ffffff',
  // Красный как текст на тёмно-сером: на полтона светлее заливки, чтобы держать
  // контраст на `card`; это `--accent-hover` ui-kit.
  primaryInk: BRAND.redHover,
  primarySoft: '#d718202e',
  primarySofter: '#d7182017',

  secondary: '#3d3d3d',
  secondaryForeground: '#e8e8e8',
  textSecondary: '#b3b3b3',
  muted: '#00000038',
  mutedForeground: '#808080',
  accent: '#ffffff0f',
  accentForeground: '#e8e8e8',

  signal: '#46b552',
  signalForeground: '#ffffff',
  signalInk: '#46b552',
  signalSoft: '#46b55229',
  warning: '#f2a93b',
  warningForeground: '#ffffff',
  warningInk: '#f2a93b',
  warningSoft: '#f2a93b29',
  destructive: '#ff5c5c',
  destructiveForeground: '#ffffff',
  destructiveInk: '#ff5c5c',
  destructiveSoft: '#ff5c5c29',
  info: '#4a9eea',
  infoInk: '#4a9eea',
  infoSoft: '#4a9eea29',
  neutralSoft: '#b3b3b31f',

  borderSubtle: '#ffffff0f',
  border: '#ffffff1f',
  borderStrong: '#ffffff3d',
  input: '#ffffff1f',
  ring: BRAND.red,

  gridHeader: '#00000038',
  toolbar: '#00000038',

  overlay: '#000000a8',
  skeleton: '#ffffff0f',
};

export const light: Palette = {
  // Оправа на свету светлеет вместе с темой — shadcn `--sidebar`: на полтона светлее
  // страницы (`background`), чтобы её край был виден и без линии. Поднятое на ней
  // (активный раздел) — на полтона темнее; линии, как и везде на свету, сплошные.
  chrome: '#fafafa',
  chromeRaised: '#ebebeb',
  chromeForeground: '#262626',
  chromeMuted: '#6e6e6e',
  chromeBorder: '#d4d4d4',
  chromeHover: '#00000012',
  chromeSunken: '#0000000a',
  chromeAccent: BRAND.red,

  sunken: '#e3e3e3',
  background: '#f0f0f0',
  foreground: '#262626',
  card: '#ffffff',
  cardForeground: '#262626',
  elevated: '#ffffff',
  popover: '#ffffff',
  inset: '#00000009',
  active: '#00000012',

  primary: BRAND.red,
  primaryForeground: '#ffffff',
  primaryInk: '#c2161d',
  primarySoft: '#d718201f',
  primarySofter: '#d718200f',

  secondary: '#f0f0f0',
  secondaryForeground: '#262626',
  textSecondary: '#595959',
  muted: '#00000009',
  mutedForeground: '#8c8c8c',
  accent: '#0000000a',
  accentForeground: '#262626',

  signal: '#2e9e3a',
  signalForeground: '#ffffff',
  signalInk: '#2e9e3a',
  signalSoft: '#2e9e3a21',
  warning: '#c77700',
  warningForeground: '#ffffff',
  warningInk: '#c77700',
  warningSoft: '#c7770024',
  destructive: '#d93434',
  destructiveForeground: '#ffffff',
  destructiveInk: '#d93434',
  destructiveSoft: '#d934341f',
  info: '#2f7fd1',
  infoInk: '#2f7fd1',
  infoSoft: '#2f7fd11f',
  neutralSoft: '#5959591a',

  // На свету границы — сплошные линии, а не полупрозрачные: полупрозрачная сетка
  // таблицы на белом фоне исчезает.
  borderSubtle: '#e8e8e8',
  border: '#d4d4d4',
  borderStrong: '#b0b0b0',
  input: '#d4d4d4',
  ring: BRAND.red,

  gridHeader: '#00000009',
  toolbar: '#00000009',

  overlay: '#00000073',
  skeleton: '#0000000f',
};

/**
 * Радиусы — `--radius-*` ui-kit. Углы почти прямые: интерфейс собирается из
 * панелей, врезанных друг в друга, а не из отдельно лежащих карточек.
 *
 *      xs 2   sm 2   md 3   lg 3   xl 4
 *
 * `pill` — только для того, что круглое по природе: точка состояния, ползунок.
 * Метка, кнопка и поле круглыми не бывают.
 */
export const radii = {
  xs: 2,
  sm: 2,
  md: 3,
  lg: 3,
  xl: 4,
  pill: 999,
} as const;

/** Back-compat alias: `radius` is the control default. */
export const radius = radii.md;

/** 4pt grid. spacing(3) === 12. */
export const spacing = (n: number) => n * 4;

/** The one-line separator weight used across the app. */
export const hairline = StyleSheet.hairlineWidth;

/**
 * Высоты органов управления — `--control-height*` ui-kit плюс палец.
 *
 * Одна высота у кнопки, поля и списка: форма выглядит собранной из одного набора.
 * `xs`/`sm`/`md` — это sm/md/lg рабочей станции (24/28/34). `lg` — 44: пол касания;
 * контрол, в который на телефоне попадают пальцем, — поле, «Сохранить» — берёт его.
 */
export const controls = {
  xs: 24,
  sm: 28,
  md: 34,
  lg: 44,
} as const;

/**
 * Layout constants.
 *
 * `maxContentWidth` matters because this same code renders in a DESKTOP BROWSER, and
 * on a phone-width window an uncapped list row would still be phone-width. It is the
 * cap for a NARROW window, not for a desktop one: past the desktop breakpoint there
 * is deliberately no ceiling — рабочая станция не оставляет полей по краям, лишняя
 * ширина уходит колонкам таблицы. A screen that genuinely needs a reading column asks
 * for one (`width="form"`).
 */
export const layout = {
  /** Reading width on a phone — and the cap for a narrow browser window. */
  maxContentWidth: 560,
  /** The column a single record or a settings page is read in, once the window is wide. */
  formContentWidth: 820,
  /** Форма в две колонки — главное и сбоку (фото, срок): 820 на двоих мало. */
  wideFormContentWidth: 1080,
  /** Auth and onboarding: one short task, and it never widens. */
  authContentWidth: 420,
  gutter: spacing(4),
  /** Поля страницы на широком окне — `--space-4` ui-kit, панели почти встык с краями. */
  desktopGutter: spacing(5),
  /** Height of the tab bar's content, before the bottom safe-area inset. */
  tabBarHeight: 56,
  /** `--sidebar-width` */
  sidebarWidth: 232,
  /** `--sidebar-width-collapsed` */
  sidebarCollapsedWidth: 56,
  /** `--header-height` — верхняя полоса рабочей станции. */
  titleBarHeight: 44,
  /** `--statusbar-height` */
  statusBarHeight: 26,
} as const;

/* ──────────────────────────────────────────────────────────────────────────
   Elevation
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Тени: почти нет. Панели разделяются линиями; всплывающее — плотной тенью, чтобы
 * отличаться от врезанного. Это `--shadow-*` ui-kit, по одному слою на ступень.
 */
type Shadow = Pick<
  ViewStyle,
  'shadowColor' | 'shadowOffset' | 'shadowOpacity' | 'shadowRadius' | 'elevation'
>;

function shadow(y: number, blur: number, opacity: number, elevation: number): Shadow {
  return Platform.select<Shadow>({
    web: {
      // RNW maps the quartet to box-shadow; passing it directly keeps one code path.
      shadowColor: '#000000',
      shadowOffset: {width: 0, height: y},
      shadowOpacity: opacity,
      shadowRadius: blur,
    },
    default: {
      shadowColor: '#000000',
      shadowOffset: {width: 0, height: y},
      shadowOpacity: opacity,
      shadowRadius: blur,
      elevation,
    },
  }) as Shadow;
}

/** `--shadow-xs: none` — ни у кнопки, ни у карточки тени нет. */
const NONE: Shadow = {};

export const shadows = {
  /** Кнопка, метка, поле: ничего. */
  sm: NONE,
  /** Панель: ничего — её рисует рамка. */
  panel: NONE,
  /** Наведение: ничего — меняется заливка. */
  lift: NONE,
  /** `--shadow-md` — всплывающее: меню, тост. */
  pop: shadow(6, 18, 0.5, 8),
  /** `--shadow-lg` — модальное окно. */
  float: shadow(18, 48, 0.65, 16),
  /* Back-compat aliases. */
  md: NONE,
  lg: shadow(6, 18, 0.5, 8),
} as const;

export const theme = {
  colors: {light, dark},
  radii,
  radius,
  spacing,
  controls,
  shadows,
  layout,
} as const;

/**
 * The palette in force.
 *
 * Which one that is comes from `theme-mode`, not from the device directly: the app has
 * its own light/dark/system setting, and `system` is what defers to `useColorScheme()`.
 */
export function useTheme(): Palette {
  return useResolvedScheme() === 'dark' ? dark : light;
}

/** True when the dark palette is in play — for the handful of tone-dependent choices. */
export function useIsDark(): boolean {
  return useResolvedScheme() === 'dark';
}
